<script lang="ts">
  import { page } from '@inertiajs/svelte';
  import { apiClient } from '@/utils/api';

  type Capture = {
    id: string;
    email: string;
    name: string | null;
    source: string;
    createdAt: string;
  };

  type View = 'list' | 'compose';

  let captures = $state<Capture[]>([]);
  let loading = $state(false);
  let error = $state('');
  let view = $state<View>('list');

  // Newsletter compose state
  let subject = $state('');
  let body = $state('');
  let sending = $state(false);
  let sendResult = $state<{
    enqueuedCount?: number;
    recipientCount?: number;
    failedCount?: number;
    pending?: boolean;
    message?: string;
    error?: string;
  } | null>(null);

  // 발송 멱등키. 같은 시도의 재시도(네트워크 실패·중복 클릭)는 같은 키를 보내
  // 서버가 재발송 대신 첫 결과를 돌려주게 한다.
  //
  // 🔴 메모리에만 두면 안 된다. 서버가 적재를 끝냈는데 응답이 오기 전에 탭이
  //    닫히거나 브라우저가 죽으면, 다시 열었을 때 새 키가 생겨 **전원에게 두 번째
  //    메일**이 나간다. 그래서 localStorage 에도 남긴다.
  // 🔴 키는 **원고(제목+본문)에 묶는다.** 시간으로만 만료시키면 두 방향으로 다 틀린다 —
  //    너무 짧으면 미해결 발송의 키가 사라져 재클릭이 전원 재발송이 되고,
  //    너무 길면 다음에 쓰는 다른 뉴스레터가 옛 키를 물려받아 조용히 replay 된다.
  //    원고가 같으면 "같은 발송의 재시도", 다르면 "다른 발송"이다.
  // 🔴 저장 키는 **계정별**. 한 브라우저를 두 계정이 쓰면 남의 키를 물려받는다.
  //    username 이 아니라 바뀌지 않는 user id 로 나눈다 — username 은 비어 있을 수도
  //    있고 바뀔 수도 있어서, 진행 중인 발송이 도중에 다른 키로 갈라진다.
  const IDEMPOTENCY_TTL_MS = 24 * 60 * 60 * 1000;

  type KeyRecord = { key: string; subject: string; body: string; createdAt: number };

  let memoryKeyRecord: KeyRecord | null = null;

  const storageKey = $derived(
    `axhub:newsletter:idempotency:${page.props.auth?.user?.id ?? 'anonymous'}`,
  );

  function isFresh(record: KeyRecord): boolean {
    return Date.now() - record.createdAt < IDEMPOTENCY_TTL_MS;
  }

  function parseRecord(raw: string | null): KeyRecord | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<KeyRecord>;
      if (!parsed.key || typeof parsed.createdAt !== 'number') return null;
      const record: KeyRecord = {
        key: parsed.key,
        subject: parsed.subject ?? '',
        body: parsed.body ?? '',
        createdAt: parsed.createdAt,
      };
      return isFresh(record) ? record : null;
    } catch {
      return null;
    }
  }

  function readStoredRecord(): KeyRecord | null {
    try {
      return parseRecord(localStorage.getItem(storageKey));
    } catch {
      return null;
    }
  }

  function writeRecord(record: KeyRecord | null) {
    memoryKeyRecord = record;
    try {
      if (record === null) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      // 저장 못 해도 발송은 진행한다 (사생활 보호 모드·용량 초과).
      // 그 경우 이 탭이 살아 있는 동안은 memoryKeyRecord 가 재시도를 지켜 준다.
    }
  }

  function matches(record: KeyRecord | null, subject: string, body: string): boolean {
    return record !== null && isFresh(record) && record.subject === subject && record.body === body;
  }

  // 다른 탭이 키를 만들거나 지우면 따라간다. 같은 계정의 두 탭이 각자 키를 만들면
  // 캠페인이 둘 생겨 구독자가 메일을 두 번 받는다.
  $effect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      memoryKeyRecord = parseRecord(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  });

  function idempotencyKeyFor(subject: string, body: string): string {
    // 메모리 캐시도 나이를 본다 — 탭을 하루 넘게 열어 둔 채 재시도하면
    // 만료된 키를 계속 쓰게 된다.
    if (matches(memoryKeyRecord, subject, body)) return memoryKeyRecord!.key;

    // 다른 탭이 같은 원고로 이미 키를 만들었으면 그것을 따른다.
    const stored = readStoredRecord();
    if (matches(stored, subject, body)) {
      memoryKeyRecord = stored;
      return stored!.key;
    }

    const key =
      globalThis.crypto?.randomUUID?.() ??
      `nl_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    writeRecord({ key, subject, body, createdAt: Date.now() });
    return key;
  }

  // Delete state
  let deletingId = $state<string | null>(null);

  // 🔴 apiClient 는 성공 응답 본문을 **최상위로 펼친다** (`{ success: true, ...json }`).
  //    `res.data` 만 보면 항상 비어 목록이 늘 0명이 되고, 그러면 발송 버튼도
  //    영구 비활성이다. LinksTab 의 `responseLink` 와 같은 관용구로 둘 다 본다.
  function responseCaptures(response: { captures?: Capture[]; data?: { captures?: Capture[] } }) {
    return response.captures || response.data?.captures || null;
  }

  type SendPayload = {
    enqueuedCount?: number;
    recipientCount?: number;
    failedCount?: number;
    pending?: boolean;
    message?: string;
  };

  function sendPayload(response: SendPayload & { data?: SendPayload }): SendPayload {
    return {
      enqueuedCount: response.enqueuedCount ?? response.data?.enqueuedCount,
      recipientCount: response.recipientCount ?? response.data?.recipientCount,
      failedCount: response.failedCount ?? response.data?.failedCount,
      pending: response.pending ?? response.data?.pending,
      message: response.message ?? response.data?.message,
    };
  }

  async function loadCaptures() {
    loading = true;
    error = '';
    try {
      const res = await apiClient.get<{ captures: Capture[] }>('/api/v1/email_captures');
      captures = responseCaptures(res) ?? [];
    } catch {
      error = '구독자 목록을 불러오지 못했습니다.';
    } finally {
      loading = false;
    }
  }

  async function deleteCapture(id: string) {
    if (!confirm('이 구독자를 삭제하시겠습니까?')) return;
    deletingId = id;
    try {
      await apiClient.delete(`/api/v1/email_captures/${id}`);
      captures = captures.filter((c) => c.id !== id);
    } catch {
      alert('삭제에 실패했습니다.');
    } finally {
      deletingId = null;
    }
  }

  async function sendNewsletter() {
    if (!subject.trim() || !body.trim()) return;
    sending = true;
    sendResult = null;
    try {
      const res = await apiClient.post<SendPayload>('/api/v1/email_captures/send_newsletter', {
        subject: subject.trim(),
        body: body.trim(),
        idempotencyKey: idempotencyKeyFor(subject.trim(), body.trim()),
      });

      // apiClient 는 던지지 않는다 — 실패도 `{ success: false, message }` 로 온다.
      if (!res.success) {
        // 409 = 같은 키의 앞선 요청이 아직 처리 중. 실패가 아니라 "다시 눌러보세요"다.
        if (res.status === 409) {
          sendResult = { pending: true, message: res.message };
        } else {
          sendResult = { error: res.message || '발송에 실패했습니다.' };
        }
        // 키는 남겨 둔다(localStorage 포함). 같은 키로 다시 눌러야 서버가 중복
        // 발송을 막을 수 있다.
        return;
      }

      const payload = sendPayload(res);
      sendResult = {
        enqueuedCount: payload.enqueuedCount,
        recipientCount: payload.recipientCount,
        failedCount: payload.failedCount,
        pending: payload.pending,
        message: payload.message ?? res.message,
      };

      // 서버가 발송 결과를 확인하지 못한 캠페인. 같은 키로는 서버가 다시 보내지
      // 않으므로 키를 유지해 재클릭이 중복 발송이 되지 않게 하고, 원고도 남긴다.
      if (payload.pending) return;

      // 🔴 일부만 예약된 경우에도 **키를 지우지 않는다.** 키를 지우면 다시 누를 때
      //    새 캠페인이 만들어져 이미 받은 사람에게 두 번째 메일이 간다.
      //    못 받은 사람만 골라 재개하는 것은 서버가 아직 못 한다 — 그때까지는
      //    중복을 만들지 않는 쪽이 맞다. 원고는 남겨 상황을 알 수 있게 한다.
      if ((payload.failedCount ?? 0) > 0) return;

      writeRecord(null);
      subject = '';
      body = '';
    } catch (e: unknown) {
      const err = e as { message?: string };
      sendResult = { error: err?.message || '발송에 실패했습니다.' };
    } finally {
      sending = false;
    }
  }

  function exportCsv() {
    window.open('/api/v1/email_captures/export', '_blank');
  }

  $effect(() => {
    loadCaptures();
  });

  const sourceLabel: Record<string, string> = {
    profile: '프로필',
    public_profile: '공개 프로필',
    qr_code: 'QR 코드',
    short_link: '단축 링크',
  };
</script>

<div class="space-y-6">
  <!-- Header -->
  <div class="rounded-2xl border border-gray-200 bg-white p-6">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-950">구독자 관리</h2>
        <p class="mt-1 text-sm text-gray-500">
          프로필 페이지에서 이메일을 등록한 구독자 목록입니다.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span class="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">
          총 {captures.length}명
        </span>
        <button
          type="button"
          onclick={exportCsv}
          class="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        >
          CSV 내보내기
        </button>
        <button
          type="button"
          onclick={() => { view = view === 'compose' ? 'list' : 'compose'; sendResult = null; }}
          class={`rounded-full px-4 py-2 text-sm font-medium transition ${
            view === 'compose'
              ? 'bg-gray-200 text-gray-700'
              : 'bg-gray-950 text-white hover:bg-gray-800'
          }`}
        >
          {view === 'compose' ? '목록으로' : '뉴스레터 보내기'}
        </button>
      </div>
    </div>
  </div>

  {#if view === 'compose'}
    <!-- Newsletter Compose -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6">
      <h3 class="mb-4 text-base font-semibold text-gray-950">뉴스레터 작성</h3>

      {#if sendResult}
        <div
          class={`mb-4 rounded-xl px-4 py-3 text-sm font-medium ${
            sendResult.error
              ? 'bg-red-50 text-red-700'
              : (sendResult.failedCount ?? 0) > 0 || sendResult.pending
                ? 'bg-amber-50 text-amber-700'
                : 'bg-green-50 text-green-700'
          }`}
        >
          {sendResult.error ?? sendResult.message}
        </div>
      {/if}

      <div class="space-y-4">
        <div>
          <label for="nl-subject" class="mb-1.5 block text-sm font-medium text-gray-700">
            제목
          </label>
          <input
            id="nl-subject"
            type="text"
            bind:value={subject}
            placeholder="이메일 제목을 입력하세요"
            class="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
          />
        </div>

        <div>
          <label for="nl-body" class="mb-1.5 block text-sm font-medium text-gray-700">
            내용
          </label>
          <textarea
            id="nl-body"
            bind:value={body}
            rows={10}
            placeholder="구독자에게 전달할 내용을 입력하세요."
            class="w-full resize-y rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200"
          ></textarea>
        </div>

        <div class="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <p class="text-xs text-gray-400">
            현재 구독자 <strong class="text-gray-600">{captures.length}명</strong>에게 발송됩니다.
          </p>
          <button
            type="button"
            onclick={sendNewsletter}
            disabled={sending || !subject.trim() || !body.trim() || captures.length === 0}
            class="rounded-full bg-gray-950 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? '발송 중...' : `${captures.length}명에게 발송`}
          </button>
        </div>
      </div>
    </div>
  {:else}
    <!-- Subscriber List -->
    <div class="rounded-2xl border border-gray-200 bg-white">
      {#if loading}
        <div class="flex items-center justify-center py-16 text-sm text-gray-400">
          불러오는 중...
        </div>
      {:else if error}
        <div class="flex flex-col items-center gap-3 py-16">
          <p class="text-sm text-red-500">{error}</p>
          <button
            type="button"
            onclick={loadCaptures}
            class="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            다시 시도
          </button>
        </div>
      {:else if captures.length === 0}
        <div class="flex flex-col items-center gap-2 py-16">
          <p class="text-sm font-medium text-gray-600">아직 구독자가 없습니다</p>
          <p class="text-xs text-gray-400">프로필 페이지에서 이메일 등록을 켜두면 구독자가 쌓입니다.</p>
        </div>
      {:else}
        <!-- Table header -->
        <div class="grid grid-cols-[1fr_1fr_auto_auto] gap-3 border-b border-gray-100 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
          <span>이메일</span>
          <span>이름 / 소스</span>
          <span>등록일</span>
          <span></span>
        </div>

        <ul class="divide-y divide-gray-50">
          {#each captures as capture (capture.id)}
            <li class="grid grid-cols-[1fr_1fr_auto_auto] items-center gap-3 px-6 py-3.5">
              <span class="truncate text-sm font-medium text-gray-900">{capture.email}</span>
              <span class="flex flex-col gap-0.5">
                <span class="text-sm text-gray-700">{capture.name || '—'}</span>
                <span class="text-xs text-gray-400">{sourceLabel[capture.source] ?? capture.source}</span>
              </span>
              <span class="text-xs text-gray-400 whitespace-nowrap">
                {new Date(capture.createdAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })}
              </span>
              <button
                type="button"
                onclick={() => deleteCapture(capture.id)}
                disabled={deletingId === capture.id}
                class="rounded-lg px-2 py-1 text-xs text-gray-400 transition hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                aria-label="삭제"
              >
                {deletingId === capture.id ? '...' : '삭제'}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>
